import { LoginCredentials, AuthResponse, User } from './types';
import { GoogleAuthProvider, signInWithPopup, signInWithEmailAndPassword, createUserWithEmailAndPassword, updateProfile, deleteUser, onAuthStateChanged } from "firebase/auth";
import { auth } from '../firebase/firebaseConfig';

// Global function to get invite code
const getInviteCode = (): string | null => {
  return localStorage.getItem('inviteCode');
};

// Check if user is already associated with a company
const isUserAssociated = async (uid: string): Promise<boolean> => {
  try {
    const apiKey = import.meta.env.VITE_FOS_API_KEY;
    const apiUrl = import.meta.env.VITE_FOS_URL;
    
    const response = await fetch(`${apiUrl}/spasense/auth/checkUserAssociation`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": apiKey,
      },
      body: JSON.stringify({ uid }),
    });
    
    const data = await response.json();
    return data.result;
  } catch (error) {
    console.error('Error checking user association:', error);
    return false;
  }
};

// Associate user with company based on invite code
const associateUserWithCompany = async (uid: string): Promise<boolean> => {
  try {
    // Check if user is already associated
    const alreadyAssociated = await isUserAssociated(uid);
    if (alreadyAssociated) {
      return true;
    }
    
    const inviteCode = getInviteCode();
    if (!inviteCode) {
      return false;
    }
    
    const apiKey = import.meta.env.VITE_FOS_API_KEY;
    const apiUrl = import.meta.env.VITE_FOS_URL;
    
    const response = await fetch(`${apiUrl}/spasense/auth/associateUser`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": apiKey,
      },
      body: JSON.stringify({ 
        uid, 
        inviteCode 
      }),
    });
    
    const data = await response.json();
    // Remove invite code from localStorage
    localStorage.removeItem('inviteCode');

    return data.result;
  } catch (error) {
    console.error('Error associating user with company:', error);
    return false;
  }
};

const deleteUserRecord = async () => {
  try {
    // Delete the user from Firebase
    const user = auth.currentUser;
    if (user) {
      await deleteUser(user);
    }
  } catch (error) {
    console.error('Error deleting user:', error);
  }
};

// Login with email and password
export const login = async (credentials: LoginCredentials): Promise<AuthResponse> => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, credentials.email, credentials.password);
    const user = userCredential.user;

    // Store auth token in localStorage
    const token = await user.getIdToken();
    localStorage.setItem('authToken', token);
    localStorage.setItem('user', JSON.stringify(user));
    
    return {
      success: true,
      user: {
        id: user.uid,
        email: user.email || '',
        name: user.displayName || '',
        photoURL: user.photoURL || '',
        customClaims: {
          isAdmin: false,
        },
      },
    };
  } catch (error) {
    console.error('Login error:', error);
    return {
      success: false,
      error: error.message || 'An unexpected error occurred',
    };
  }
};

// Login with Google using async/await
export const loginWithGoogle = async (): Promise<AuthResponse> => {
  try {
    const provider = new GoogleAuthProvider();
    
    // Use await with signInWithPopup
    const result = await signInWithPopup(auth, provider);
    
    // Get the Google Access Token
    const credential = GoogleAuthProvider.credentialFromResult(result);
    const token = credential?.accessToken;
    
    // Get the signed-in user info
    const user = result.user;
    
    // Store auth token in localStorage
    const idToken = await user.getIdToken();
    localStorage.setItem('authToken', idToken);
    localStorage.setItem('user', JSON.stringify(user));

    // Associate user with company
    const associated = await associateUserWithCompany(user.uid);

    if (!associated) {
      // Delete the user
      await deleteUserRecord();
      return {
        success: false,
        error: 'Please try again or use email instead',
      };
    }
  
    // Return success response
    return {
      success: true,
      user: {
        id: user.uid,
        email: user.email || '',
        name: user.displayName || '',
        photoURL: user.photoURL || '',
        customClaims: {
          isAdmin: false,
        },
      },
      token: token || undefined,
    };
  } catch (error) {
    // Handle errors
    console.error('Google login error:', error);
    
    // Extract error details
    const errorCode = error.code;
    const errorMessage = error.message;
    const email = error.customData?.email;
    const credential = GoogleAuthProvider.credentialFromError(error);
    
    console.error(errorCode, errorMessage, email, credential);
    return {
      success: false,
      error: errorMessage || 'Google login failed',
    };
  }
};

// Logout
export const logout = (): void => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('user');
  auth.signOut();
  // Add any other cleanup needed
};

// Check if user is authenticated
export const isAuthenticated = (): boolean => {
  return !!localStorage.getItem('authToken');
};

// Get current auth token
export const getToken = async (): Promise<string | null> => {
  const user = auth.currentUser;
  if (user) {
    try {
      // Force refresh the token
      const token = await user.getIdToken(true);
      localStorage.setItem('authToken', token);
      return token;
    } catch (error) {
      console.error('Error refreshing token:', error);
      return null;
    }
  }
  return null;
};

export const validateInviteCode = async (inviteCode: string): Promise<boolean> => {
  try {
    const apiKey = import.meta.env.VITE_FOS_API_KEY;
    const apiUrl = import.meta.env.VITE_FOS_URL;
    const response = await fetch(`${apiUrl}/spasense/auth/validateInviteCode`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": apiKey,
      },
      body: JSON.stringify({ inviteCode }),
    });
    const data = await response.json();

    localStorage.setItem('inviteCode', inviteCode);

    return data.result;
  } catch (error) {
    console.error('Error validating invite code:', error);
    return false;
  }
};

// Define the SignupCredentials interface in your types.ts file or add it here
export interface SignupCredentials {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

// Signup with email and password
export const signup = async (credentials: SignupCredentials): Promise<AuthResponse> => {
  try {
    
    // Create user with email and password
    const userCredential = await createUserWithEmailAndPassword(
      auth, 
      credentials.email, 
      credentials.password
    );
    
    const user = userCredential.user;
    
    // Update the user's profile with first and last name
    await updateProfile(user, {
      displayName: `${credentials.firstName} ${credentials.lastName}`
    });
    
    // Store auth token in localStorage
    const token = await user.getIdToken();
    localStorage.setItem('authToken', token);
    
    // Associate user with company
    const associated = await associateUserWithCompany(user.uid);
    
    if (!associated) {
      // Delete the user
      await deleteUserRecord();
      return {
        success: false,
        error: 'There was an error associating your account with a company. Please contact support.',
      };
    }
    
    return {
      success: true,
      user: {
        id: user.uid,
        email: user.email || '',
        name: `${credentials.firstName} ${credentials.lastName}`,
        photoURL: user.photoURL || '',
        customClaims: {
          isAdmin: false,
        },
      },
    };
  } catch (error) {
    console.error('Signup error:', error);
    
    // Handle specific Firebase error codes
    if (error.code === 'auth/email-already-in-use') {
      return {
        success: false,
        error: 'This email is already registered. Please login instead.',
      };
    } else if (error.code === 'auth/weak-password') {
      return {
        success: false,
        error: 'Password is too weak. Please use a stronger password.',
      };
    }
    
    return {
      success: false,
      error: error.message || 'An unexpected error occurred during signup',
    };
  }
};

export const getCurrentUser = () => {
  const user = auth.currentUser;
  if (user) {
    return user;
  } else {
    return JSON.parse(localStorage.getItem('user') || '{}');
  }
};

// Function to initialize auth state listener
export const initializeAuthListener = (onUserAuthenticated: (user: User) => void, onUserUnauthenticated: () => void) => {
  onAuthStateChanged(auth, (user) => {
    if (user) {
      // User is signed in
      onUserAuthenticated({
        id: user.uid,
        email: user.email || '',
        name: user.displayName || '',
        photoURL: user.photoURL || '',
        customClaims: {
          isAdmin: false,
        },
      });
    } else {
      // User is signed out
      onUserUnauthenticated();
    }
  });
};